import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, CheckCircle, Eye, FileCheck, FileText, FileType2Icon, SquarePen, User, Users } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useGetArchitect, useGetSubcontractor, useCompleteArchitectOnboarding, useCompleteSubcontractorOnboarding } from '@/hooks/use-contractors';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';

export default function ReviewStep() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { handleError } = useErrorHandler();

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';
    const currentParams = new URLSearchParams(searchParams.toString());

    // Fetch organization data based on account type
    const { data: architectData, isLoading: isLoadingArchitect } = useGetArchitect(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorData, isLoading: isLoadingSubcontractor } = useGetSubcontractor(
        accountType === 'subcontractor' ? organizationId : null
    );

    // Get the current organization data
    const organizationData = accountType === 'architect' ? architectData : subcontractorData;
    const specificProfile = accountType === 'architect'
        ? architectData?.architectSpecificProfile
        : subcontractorData?.subcontractorSpecificProfile;
    const profile = organizationData?.profile;

    // Complete onboarding mutations
    const completeArchitectOnboarding = useCompleteArchitectOnboarding();
    const completeSubcontractorOnboarding = useCompleteSubcontractorOnboarding();

    const handleSubmit = async (): Promise<boolean> => {
        if (!organizationId) {
            toast({
                title: 'Error',
                description: 'Organization ID is missing. Please try again.',
                variant: 'destructive',
            });
            return false;
        }

        try {
            let response;

            if (accountType === 'architect') {
                response = await completeArchitectOnboarding.mutateAsync(organizationId);
            } else {
                response = await completeSubcontractorOnboarding.mutateAsync(organizationId);
            }

            toast({
                title: 'Onboarding Completed!',
                description: 'Your profile has been successfully created.',
            });

            // Navigate to the success page with tokens in state
            navigate(
                `/onboarding/success${organizationId ? `?orgId=${organizationId}&type=${accountType}` : ''}`,
                {
                    state: {
                        tokens: {
                            access_token: response.access_token,
                            refresh_token: response.refresh_token,
                        },
                        user: response.user,
                        accountType: accountType,
                        referenceNumber: response.referenceNumber,
                    },
                }
            );
            return true;
        } catch (error: any) {
            handleError(error, {
                customMessage: 'Failed to complete onboarding. Please try again.',
            });
            return false;
        }
    };

    // Show loading state
    if (isLoadingArchitect || isLoadingSubcontractor) {
        return (
            <OnboardingWrapper
                currentStep={7}
                totalSteps={7}
                steps={EXTENDED_STEPS}
                accountType={accountType || 'architect'}
                onNext={async () => false}
                isLoading={true}
                saveButtonText="Loading..."
            >
                <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                    <span className="ml-3 text-muted-foreground">Loading your information...</span>
                </div>
            </OnboardingWrapper>
        );
    }

    return (
        <OnboardingWrapper
            currentStep={7}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType || 'architect'}
            onNext={async () => {
                return handleSubmit();
            }}
            isLoading={completeArchitectOnboarding.isPending || completeSubcontractorOnboarding.isPending}
            saveButtonText={completeArchitectOnboarding.isPending || completeSubcontractorOnboarding.isPending ? 'Completing Setup...' : 'Complete Setup'}
        >
            <div className="space-y-6">
                <div className="mb-6">
                    <h3 className="text-xl font-bold tracking-tight text-foreground mb-2">
                        Review Your Information
                    </h3>
                    <p className="text-muted-foreground">
                        Please review all the information you've provided. You can edit any section by clicking the "Edit" button.
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Business Information */}
                    <Card className="border border-border">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                            <div className="flex items-center space-x-2">
                                <Building2 className="w-5 h-5" />
                                <CardTitle className="text-sm font-medium text-foreground">Business Information</CardTitle>
                            </div>
                            <Button
                                variant="ghost"
                                size="default"
                                className="text-primary border border-border h-8 px-3"
                                onClick={() => navigate(`/onboarding/business-info?${currentParams.toString()}`)}
                            >
                                <SquarePen className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                                <div>
                                    <p className="text-sm font-medium text-foreground mb-1">Legal Name:</p>
                                    <p className="text-sm text-muted-foreground">{organizationData?.name || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground mb-1">DBA Name:</p>
                                    <p className="text-sm text-muted-foreground">{specificProfile?.dbaName || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground mb-1">Tax ID:</p>
                                    <p className="text-sm text-muted-foreground">{specificProfile?.taxId || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground mb-1">Website:</p>
                                    <p className="text-sm text-muted-foreground">{specificProfile?.website || 'Not provided'}</p>
                                </div>
                                <div className="col-span-2">
                                    <p className="text-sm font-medium text-foreground mb-1">Address:</p>
                                    <p className="text-sm text-muted-foreground">
                                        {profile?.address ?
                                            `${profile.address}${profile.city ? `, ${profile.city}` : ''}${profile.state ? `, ${profile.state}` : ''}${profile.zipCode ? ` ${profile.zipCode}` : ''}`
                                            : 'Not provided'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground mb-1">Email Address:</p>
                                    <p className="text-sm text-muted-foreground">{specificProfile?.primaryEmail || organizationData?.businessEmail || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground mb-1">Phone Number:</p>
                                    <p className="text-sm text-muted-foreground">{profile?.phone || 'Not provided'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Federal Tax Classification */}
                    <Card className="border border-border">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                            <div className="flex items-center space-x-2">
                                <FileText className="w-5 h-5" />
                                <CardTitle className="text-sm font-medium text-foreground">Federal Tax Classification</CardTitle>
                            </div>
                            <Button
                                variant="ghost"
                                size="default"
                                className="text-primary border border-border h-8 px-3"
                                onClick={() => navigate(`/onboarding/classification?${currentParams.toString()}`)}
                            >
                                <SquarePen className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div>
                                <p className="text-sm font-medium text-foreground mb-1">Classification:</p>
                                <p className="text-sm text-muted-foreground capitalize">{specificProfile?.federalClassification?.replace('-', ' ') || 'Not provided'}</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Primary Contact */}
                    <Card className="border border-border/15">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                            <div className="flex items-center space-x-2">
                                <User className="w-5 h-5" />
                                <CardTitle className="text-sm font-medium text-foreground">Primary Contact</CardTitle>
                            </div>
                            <Button
                                variant="ghost"
                                size="default"
                                className="text-primary border border-border h-8 px-3"
                                onClick={() => navigate(`/onboarding/contact-info?${currentParams.toString()}`)}
                            >
                                <SquarePen className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-0 pb-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                                <div>
                                    <p className="text-xs font-bold text-foreground mb-1">Name:</p>
                                    <p className="text-sm text-foreground">
                                        {specificProfile?.primaryContactFirstName && specificProfile?.primaryContactLastName
                                            ? `${specificProfile.primaryContactFirstName} ${specificProfile.primaryContactLastName}`
                                            : 'Not provided'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-foreground mb-1">Role:</p>
                                    <p className="text-sm text-foreground capitalize">{specificProfile?.primaryContactRole?.replace('role-', '') || accountType || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-foreground mb-1">Email Address:</p>
                                    <p className="text-sm text-foreground">{specificProfile?.primaryContactEmail || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-foreground mb-1">Phone Number:</p>
                                    <p className="text-sm text-foreground">{specificProfile?.primaryContactPhone || 'Not provided'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Documents */}
                    <Card className="border border-border/15">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                            <div className="flex items-center space-x-2">
                                <FileCheck className="w-5 h-5" />
                                <CardTitle className="text-sm font-medium text-foreground">Documents</CardTitle>
                            </div>
                            <Button
                                variant="ghost"
                                size="default"
                                className="text-primary border border-border h-8 px-3"
                                onClick={() => navigate(`/onboarding/documents?${currentParams.toString()}`)}
                            >
                                <SquarePen className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-0 pb-4">
                            <div className="grid grid-cols-2 gap-3">
                                {(() => {
                                    // Document type mapping
                                    const documentLabels = {
                                        'WORKERS_COMPENSATION_POLICY': "Worker's Compensation",
                                        'GENERAL_LIABILITY_POLICY': 'General Liability',
                                        'BUSINESS_LICENSE': 'Business License',
                                        'PROFESSIONAL_LICENSE': 'Professional License',
                                        'ARCHITECT_LOGO': 'Company Logo',
                                        'SUBCONTRACTOR_LOGO': 'Company Logo'
                                    };

                                    const requiredDocs = [
                                        'WORKERS_COMPENSATION_POLICY',
                                        'GENERAL_LIABILITY_POLICY',
                                        'BUSINESS_LICENSE',
                                        'PROFESSIONAL_LICENSE',
                                        accountType === 'architect' ? 'ARCHITECT_LOGO' : 'SUBCONTRACTOR_LOGO'
                                    ];
                                    const uploadedDocs = specificProfile?.documents || [];

                                    return requiredDocs.map((docType) => {
                                        const uploadedDoc = uploadedDocs.find((doc: any) => doc.documentType === docType);

                                        return (
                                            <div key={docType} className="bg-muted rounded-lg p-3 flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 bg-yellow-50 rounded-full flex items-center justify-center">
                                                        <FileText className="w-4 h-4 text-yellow-600" />
                                                    </div>
                                                    <span className="text-sm text-foreground font-medium">{documentLabels[docType as keyof typeof documentLabels]}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-xs px-2 py-1 rounded-full ${uploadedDoc?.documentUrl
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-card text-foreground border border-border'
                                                        }`}>
                                                        {uploadedDoc?.documentUrl ? 'Uploaded' : 'Not uploaded'}
                                                    </span>
                                                    {uploadedDoc?.documentUrl && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 p-0 bg-card border border-border hover:bg-muted"
                                                            onClick={() => window.open(uploadedDoc.documentUrl, '_blank')}
                                                            title="View document"
                                                        >
                                                            <Eye className="h-3 w-3 text-muted-foreground" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Agreements */}
                    <Card className="border border-border/15">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                            <div className="flex items-center space-x-2">
                                <FileType2Icon className="w-5 h-5" />
                                <CardTitle className="text-sm font-medium text-foreground">Agreements</CardTitle>
                            </div>
                            <Button
                                variant="ghost"
                                size="default"
                                className="text-primary border border-border h-8 px-3"
                                onClick={() => navigate(`/onboarding/agreements?${currentParams.toString()}`)}
                            >
                                <SquarePen className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-0 pb-4">
                            <div className="bg-muted rounded-lg p-3 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <FileText className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <span className="text-sm text-foreground font-medium">Professional Services Agreement:</span>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                                    <CheckCircle className='w-4 h-4' />
                                    Agreed
                                </div>
                            </div>

                            {specificProfile?.agreementAccepted && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-4 pt-3 border-t border-border">
                                    <div>
                                        <p className="text-xs font-medium text-foreground mb-1">Signature:</p>
                                        <p className="text-sm text-foreground">{specificProfile?.digitalSignature || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-foreground mb-1">Date:</p>
                                        <p className="text-sm text-foreground">
                                            {specificProfile?.signatureDate
                                                ? new Date(specificProfile.signatureDate).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })
                                                : 'Not signed'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Additional Users */}
                    <Card className="border border-border/15">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                            <div className="flex items-center space-x-2">
                                <Users className="w-5 h-5" />
                                <CardTitle className="text-sm font-medium text-foreground">Additional Users</CardTitle>

                            </div>
                            <Button
                                variant="ghost"
                                size="default"
                                className="text-primary border border-border h-8 px-3"
                                onClick={() => navigate(`/onboarding/add-users?${currentParams.toString()}`)}
                            >
                                <SquarePen className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-0 pb-4">
                            <div className="space-y-2">
                                {/* Show all users from the organization */}
                                {organizationData?.users?.map((user) => {
                                    const initials = user.fullName
                                        .split(' ')
                                        .map(name => name.charAt(0))
                                        .join('')
                                        .substring(0, 2)
                                        .toUpperCase();

                                    return (
                                        <div key={user.id} className="flex items-center justify-between py-1 px-3 bg-muted rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-primary-foreground text-xs font-medium bg-primary`}>
                                                    {initials}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-foreground">{user.fullName}</p>
                                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                                </div>
                                            </div>
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium text-blue-700 bg-blue-100`}>
                                                {user?.roles?.[0]?.roleId
                                                    ? user.roles[0].roleId.split('-').pop()?.replace(/^\w/, c => c.toUpperCase())
                                                    : 'Unknown'}
                                            </span>
                                        </div>
                                    );
                                })}

                                {/* Show message if no users exist */}
                                {(!organizationData?.users || organizationData.users.length === 0) && (
                                    <div className="text-center py-6">
                                        <p className="text-muted-foreground text-sm">No team members found</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Next Steps Banner */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <div className="flex items-start space-x-3">
                            <span className="font-semibold text-yellow-800">Next Steps: <span className="text-sm text-yellow-700 leading-relaxed">
                                After Authorization, your information will be reviewed by our team. You'll receive an email confirmation within 1-2 business days with your account status and any additional requirements.
                            </span></span>
                        </div>
                    </div>
                </div>
            </div>
        </OnboardingWrapper>
    );
}
